import { FC, useState, useEffect } from 'react';
import { useAppSelector } from 'helpers/hooks';
import { getUserData } from 'selectors/user';
import { getObjectDisplayName, formatNumberAsCurrency } from 'helpers/utils';
import { IEmailCadence, emailCadenceDictionary } from './constants';
import { getUserStripeCustomerPortal } from 'store/user/actions';
import { UpdatePaymentMethodCTA } from './UpdatePaymentMethodCTA';

interface Props {
  hasActiveStripeSubscriptions: boolean;
  topIssues: string[];
}

export const AccountInfo: FC<Props> = ({ hasActiveStripeSubscriptions, topIssues }) => {
  const userData = useAppSelector(getUserData);
  const [paymentMethodUpdateUrl, setPaymentMethodUpdateUrl] = useState('');
  const [paymentMethodError, setPaymentMethodError] = useState('');

  useEffect(() => {
    if (!userData.stripe_customer_id || paymentMethodUpdateUrl) {
      return;
    }
    getUserStripeCustomerPortal({
      return_url: window.location.href,
    })
      .then(response => {
        if (response.data?.url) {
          setPaymentMethodUpdateUrl(response.data.url);
        }
      })
      .catch(() => {
        setPaymentMethodError('Sorry, we are unable to update your payment method.');
      });
  }, [userData.stripe_customer_id]);

  return (
    <>
      <div className="row-to-column" data-dd-privacy="mask">
        <div className="account-row-item">
          <span className="h4-text mb3">Personal Details</span>
          <span className="body-small-text color-inkBlue">
            {userData.first_name} {userData.last_name}
          </span>
          <span className="body-small-text color-inkBlue mb10">{userData.email}</span>
          {userData.address1 ? (
            <>
              <span className="body-small-text color-inkBlue">
                {userData.address1}
                {userData.address2 && `, ${userData.address2}`}
              </span>
              <span className="body-small-text color-inkBlue">
                {userData.city}
                {userData.state && `, ${userData.state}`} {userData.zipcode}
              </span>
            </>
          ) : (
            <span className="body-small-text color-inkBlue">-</span>
          )}
        </div>
        <div className="account-row-item">
          <span className="h4-text mb3">Employer Details</span>
          <span className="body-small-text color-inkBlue">{userData.employer}</span>
          <span className="body-small-text color-inkBlue">{userData.occupation}</span>
          {userData.employer_address1 ? (
            <>
              <span className="body-small-text color-inkBlue">
                {userData.employer_address1}
                {userData.employer_address2 && `, ${userData.employer_address2}`}
              </span>
              <span className="body-small-text color-inkBlue">
                {userData.employer_city}, {userData.employer_state} {userData.employer_zipcode}
              </span>
            </>
          ) : (
            <span className="body-small-text color-inkBlue">-</span>
          )}
        </div>
      </div>
      <div className="row-to-column">
        <div className="account-row-item">
          <span className="h4-text mb3">Payment Method</span>
          <UpdatePaymentMethodCTA
            paymentMethodError={paymentMethodError}
            paymentMethodUpdateUrl={paymentMethodUpdateUrl}
            setPaymentMethodUpdateUrl={setPaymentMethodUpdateUrl}
            setPaymentMethodError={setPaymentMethodError}
            hasActiveStripeSubscriptions={hasActiveStripeSubscriptions}
          />
        </div>

        <div className="account-row-item">
          <span className="h4-text mb3">Preferences</span>
          <span className="body-small-text color-inkBlue">
            Monthly Budget:{' '}
            {formatNumberAsCurrency(userData.monthly_budget || 0, {
              trailingZeroDisplay: 'auto',
            })}
          </span>
          <span className="body-small-text color-inkBlue">
            Key Issues: {topIssues.join(', ') || '-'}
          </span>
          <span className="body-small-text color-inkBlue">
            Email Cadence:{' '}
            {getObjectDisplayName<IEmailCadence>(userData.email_cadence, emailCadenceDictionary)}
          </span>
        </div>
      </div>
    </>
  );
};
